import { gql } from '@apollo/client'
import { GraphQLContext } from '@moonpig/web-core-graphql'
import { logger } from '@moonpig/web-core-monitoring'
import { CustomerSegment } from '@moonpig/web-core-types-graphql'
import { EppoClient } from '@moonpig/web-core-eppo/server'
import { getCookie } from '@moonpig/web-core-browser-storage'
import { NextPageContext } from 'next'
import { CustomerSegmentQuery } from './__generated__/getCustomerSegments'

type GetCustomerSegmentsArgs = {
  isBot?: boolean
  isLoggedIn?: boolean
  graphQLContext: GraphQLContext
  eppoClient: EppoClient | null
  subjectKey: string
  nextPageContext: NextPageContext
}

type GetCustomerSegments = (
  args: GetCustomerSegmentsArgs,
) => Promise<Array<CustomerSegment>>

type FetchNonCachedCustomerSegmentsArgs = {
  graphQLContext: GraphQLContext
  logContext: string
}

const getHeader = (
  headerValue: string | string[] | undefined,
): string | undefined => {
  return Array.isArray(headerValue) ? headerValue[0] : headerValue
}

const createCustomerSegmentsLogContext = ({
  nextPageContext,
  subjectKey,
}: {
  nextPageContext: NextPageContext
  subjectKey: string
}): string => {
  const referrer = getHeader(nextPageContext.req?.headers?.referer)
  const requestUri = nextPageContext.req?.url ?? nextPageContext.asPath
  const cookieConsent = getCookie(nextPageContext, 'OptanonConsent')

  return JSON.stringify({
    session_id: subjectKey,
    referrer: referrer ?? 'not_available',
    request_uri: requestUri ?? 'not_available',
    cookie_consent: cookieConsent ?? 'not_available',
  })
}

const fetchNonCachedCustomerSegments = async ({
  graphQLContext: { query },
  logContext,
}: FetchNonCachedCustomerSegmentsArgs) => {
  logger.info(`Calling customer segments query | log_context=${logContext}`)

  try {
    const { data } = await query<CustomerSegmentQuery, Record<string, unknown>>(
      {
        query: gql`
          query CustomerSegmentQuery {
            me {
              segments
            }
          }
        `,
      },
    )

    const customerSegments = data.me.segments ?? []

    logger.info(
      `customerSegments: ${JSON.stringify(
        customerSegments,
      )} | log_context=${logContext}`,
    )
    return customerSegments
  } catch (error) {
    logger.warning(
      `Error fetching customer segment data: ${error.message} | log_context=${logContext}`,
    )
    return []
  }
}

export const getCustomerSegments: GetCustomerSegments = async ({
  isBot = false,
  isLoggedIn,
  graphQLContext,
  eppoClient,
  subjectKey,
  nextPageContext,
}) => {
  const isEnabled = eppoClient?.getBooleanAssignment(
    'enable-segments-query',
    subjectKey,
    {},
    false,
  )

  const logContext = createCustomerSegmentsLogContext({
    nextPageContext,
    subjectKey,
  })

  if (isBot || !isEnabled || !isLoggedIn) {
    logger.info(`Skipping customer segments query | log_context=${logContext}`)
    return []
  }

  const cachedSegments = getCookie(nextPageContext, 'mnpg_customer_segments')

  logger.info(
    `Cached segments cookie: ${JSON.stringify(
      cachedSegments,
    )} | log_context=${logContext}`,
  )

  if (cachedSegments) {
    logger.info(`Using cached segments cookie | log_context=${logContext}`)
    return JSON.parse(cachedSegments) as Array<CustomerSegment>
  }

  const segments = await fetchNonCachedCustomerSegments({
    graphQLContext,
    logContext,
  })

  logger.info(`Fetched segments ${segments} | log_context=${logContext}`)

  return segments
}
